/* The Tulus app: the contributor app for zakat, sadaqah, infaq and waqf (Azrin's request,
 * 15 October 2026: it had no page of its own).
 * Source: the app's listings on Google Play (my.snapnpay.tulus.android) and the App Store
 * (Malaysia, id1529924310), read on 15 October 2026: the description, the release notes
 * (takaful payments, Quran recitation, prayer times by location) and the reviews.
 *
 * Wording (root AGENTS.md 14b):
 *  - Store figures carry their date and link to the listing they come from. Update
 *    STORE_FIGURES.asOf whenever the numbers change; never round them up.
 *  - The listing calls the app "patuh syariah"; this site says only what Lariba Consulting
 *    confirmed on 19 August 2026 (content/syariah.ts), and links the letter.
 *  - "What people say" sums up recurring points from public reviews in our own words. No
 *    reviewer names and no quotes until a reviewer agrees to be quoted.
 *  - Organisations appear only through the approved list (content/organisations.ts).
 *  - Pictures are the site's own app images (public/images/app_img2-4.webp); the store
 *    screenshots that name unapproved organisations are not used. */
import type { Language } from "../components/useLanguage";

/** As shown on each listing on `asOf`. */
export const STORE_FIGURES = {
  asOf: "2026-10-15",
  googlePlay: { rating: "4.9", reviews: "6.84K", downloads: "50K+" },
  appStore: { rating: "4.9", ratings: "3.7k" },
};

const EN = {
  meta: {
    title: "Tulus App | Zakat, Sadaqah, Infaq and Waqf Online | Tulus",
    description:
      "Pay zakat, give sadaqah and infaq, and contribute to waqf with the Tulus app, from your phone at any time. Free on iPhone and Android.",
  },
  hero: {
    eyebrow: "The Tulus app",
    title: "Zakat, sadaqah, infaq and waqf, in one app.",
    lead: [
      "The Tulus app lets you pay zakat and give to the causes you care about from your phone, any time of day. Choose the service, pick the organisation, enter the amount and pay.",
      "It's free on iPhone and Android.",
    ],
    imageAlt: "The Tulus app home screen, with shortcuts for zakat, sadaqah, infaq, waqf and hasanah.",
    appStore: "Download the Tulus app on the App Store",
    googlePlay: "Get the Tulus app on Google Play",
  },
  ratings: {
    title: "Rated 4.9 by the people who use it",
    googlePlay: "Google Play",
    appStore: "App Store (Malaysia)",
    reviews: (n: string) => `${n} reviews`,
    ratingsCount: (n: string) => `${n} ratings`,
    downloads: (n: string) => `${n} downloads`,
    stars: "out of 5",
    source: (date: string) => `As shown on Google Play and the App Store (Malaysia) on ${date}.`,
  },
  features: {
    title: "What you can do in the app",
    items: [
      { title: "Pay zakat", body: "Pay zakat to the participating zakat institution of your choice, without copying account numbers." },
      { title: "Sadaqah and infaq", body: "Give to the mosques, causes and organisations listed in the app." },
      { title: "Waqf", body: "Contribute to waqf with participating waqf institutions." },
      { title: "Takaful", body: "Pay takaful contributions in the app." },
      { title: "Several payments at once", body: "Make more than one payment in a single session." },
      { title: "Quran and prayer times", body: "Listen to Quran recitation, and see prayer times for where you are." },
    ],
  },
  how: {
    title: "How giving works",
    steps: [
      { body: "Open the app and choose zakat, sadaqah, infaq or waqf." },
      { body: "Pick the organisation you want to give to." },
      { body: "Enter the amount and pay online." },
      { body: "Your payment is confirmed in the app straight away." },
    ],
    screens: [
      { alt: "The Tulus app home screen", caption: "Choose a service" },
      { alt: "The Tulus app list of zakat organisations to choose from", caption: "Pick the organisation" },
    ],
  },
  reviews: {
    title: "What people say",
    intro: "Reviews on Google Play and the App Store come back to the same few things.",
    items: [
      { title: "Fast and easy", body: "Reviewers call the app fast, user-friendly and convenient." },
      { title: "Pay by intention", body: "You choose what the payment is for and pay, without copying account numbers from one app to another." },
      { title: "Friendly and to the point", body: "People say the app is friendly and gets straight to the point." },
    ],
    googlePlay: "Read the reviews on Google Play",
    appStore: "Read the reviews on the App Store",
  },
  trust: {
    title: "Why people trust Tulus",
    syariahTitle: "Reviewed by our Syariah adviser",
    syariah: "Lariba Consulting, our Syariah adviser, confirmed on 19 August 2026 that the operations of the Tulus app are in line with Syariah principles.",
    syariahLink: "View the Syariah Certificate (PDF, in Malay)",
    companyTitle: "Run by Tulus Digital Sdn Bhd",
    company: "The app is built and run by Tulus Digital Sdn Bhd in Shah Alam, which also runs the Tulus payment gateway.",
    companyLink: "About Tulus",
    institutionsTitle: "Used by zakat and waqf institutions",
    institutions: "Zakat and waqf institutions across Malaysia take payments through Tulus.",
  },
  institutions: {
    title: "Listing your organisation",
    body: "Zakat, waqf and charitable organisations can receive contributions through the Tulus app. Talk to us about joining.",
    action: "Talk to Tulus",
    solutions: "See all Tulus solutions",
  },
  closing: {
    title: "Get the Tulus app",
    body: "Free on iPhone and Android.",
  },
};

export type TulusAppCopy = typeof EN;

const MS: TulusAppCopy = {
  meta: {
    title: "Aplikasi Tulus | Zakat, Sedekah, Infaq dan Wakaf Dalam Talian | Tulus",
    description:
      "Bayar zakat, beri sedekah dan infaq, dan sumbang kepada wakaf dengan aplikasi Tulus, dari telefon anda pada bila-bila masa. Percuma di iPhone dan Android.",
  },
  hero: {
    eyebrow: "Aplikasi Tulus",
    title: "Zakat, sedekah, infaq dan wakaf, dalam satu aplikasi.",
    lead: [
      "Dengan aplikasi Tulus, anda boleh membayar zakat dan menyumbang kepada tujuan yang anda sayangi dari telefon, pada bila-bila masa. Pilih perkhidmatan, pilih organisasi, masukkan jumlah dan bayar.",
      "Percuma di iPhone dan Android.",
    ],
    imageAlt: "Skrin utama aplikasi Tulus, dengan pintasan untuk zakat, sedekah, infaq, wakaf dan hasanah.",
    appStore: "Muat turun aplikasi Tulus di App Store",
    googlePlay: "Dapatkan aplikasi Tulus di Google Play",
  },
  ratings: {
    title: "Penarafan 4.9 daripada penggunanya",
    googlePlay: "Google Play",
    appStore: "App Store (Malaysia)",
    reviews: (n: string) => `${n} ulasan`,
    ratingsCount: (n: string) => `${n} penarafan`,
    downloads: (n: string) => `${n} muat turun`,
    stars: "daripada 5",
    source: (date: string) => `Seperti yang dipaparkan di Google Play dan App Store (Malaysia) pada ${date}.`,
  },
  features: {
    title: "Apa yang boleh anda lakukan dalam aplikasi",
    items: [
      { title: "Bayar zakat", body: "Bayar zakat kepada institusi zakat pilihan anda yang mengambil bahagian, tanpa perlu menyalin nombor akaun." },
      { title: "Sedekah dan infaq", body: "Sumbang kepada masjid, tujuan dan organisasi yang disenaraikan dalam aplikasi." },
      { title: "Wakaf", body: "Sumbang kepada wakaf melalui institusi wakaf yang mengambil bahagian." },
      { title: "Takaful", body: "Bayar sumbangan takaful dalam aplikasi." },
      { title: "Beberapa bayaran sekali gus", body: "Buat lebih daripada satu bayaran dalam satu sesi." },
      { title: "Al-Quran dan waktu solat", body: "Dengar bacaan Al-Quran, dan lihat waktu solat mengikut lokasi anda." },
    ],
  },
  how: {
    title: "Cara menyumbang",
    steps: [
      { body: "Buka aplikasi dan pilih zakat, sedekah, infaq atau wakaf." },
      { body: "Pilih organisasi yang ingin anda beri sumbangan." },
      { body: "Masukkan jumlah dan bayar dalam talian." },
      { body: "Bayaran anda disahkan dalam aplikasi serta-merta." },
    ],
    screens: [
      { alt: "Skrin utama aplikasi Tulus", caption: "Pilih perkhidmatan" },
      { alt: "Senarai organisasi zakat untuk dipilih dalam aplikasi Tulus", caption: "Pilih organisasi" },
    ],
  },
  reviews: {
    title: "Kata pengguna",
    intro: "Ulasan di Google Play dan App Store kerap menyebut perkara yang sama.",
    items: [
      { title: "Pantas dan mudah", body: "Pengulas menyifatkan aplikasi ini pantas, mesra pengguna dan memudahkan." },
      { title: "Bayar mengikut niat", body: "Anda pilih tujuan bayaran dan terus bayar, tanpa menyalin nombor akaun dari satu aplikasi ke aplikasi lain." },
      { title: "Mesra dan terus kepada tujuan", body: "Pengguna mengatakan aplikasi ini mesra dan terus kepada tujuan." },
    ],
    googlePlay: "Baca ulasan di Google Play",
    appStore: "Baca ulasan di App Store",
  },
  trust: {
    title: "Mengapa orang mempercayai Tulus",
    syariahTitle: "Disemak oleh penasihat Syariah kami",
    syariah: "Lariba Consulting, penasihat Syariah kami, mengesahkan pada 19 Ogos 2026 bahawa operasi aplikasi Tulus selaras dengan prinsip Syariah.",
    syariahLink: "Lihat Sijil Syariah (PDF)",
    companyTitle: "Dikendalikan oleh Tulus Digital Sdn Bhd",
    company: "Aplikasi ini dibina dan dikendalikan oleh Tulus Digital Sdn Bhd di Shah Alam, yang turut mengendalikan gerbang pembayaran Tulus.",
    companyLink: "Tentang Tulus",
    institutionsTitle: "Digunakan oleh institusi zakat dan wakaf",
    institutions: "Institusi zakat dan wakaf di seluruh Malaysia menerima bayaran melalui Tulus.",
  },
  institutions: {
    title: "Menyenaraikan organisasi anda",
    body: "Organisasi zakat, wakaf dan kebajikan boleh menerima sumbangan melalui aplikasi Tulus. Hubungi kami untuk menyertainya.",
    action: "Hubungi Tulus",
    solutions: "Lihat semua penyelesaian Tulus",
  },
  closing: {
    title: "Dapatkan aplikasi Tulus",
    body: "Percuma di iPhone dan Android.",
  },
};

export const TULUS_APP: Record<Language, TulusAppCopy> = { en: EN, ms: MS };
